import { roadmapReleaseIdFor } from '@featit/shared/view-engine/roadmap-release';
import { type IViewFilterSpec } from '@featit/shared/view-engine/types';
import { WAITING_STATUS } from '@featit/shared/waiting-status';
import { STATE_GROUP_ORDER } from '@featit/shared/work-items';
import type { IWorkItemFilter, WorkItemStateGroupType } from '@/services/WorkItemApi';
import type { WorkItemTabType, WorkItemUpdatedFilterType } from '@/stores/WorkItemViewStore';

/** The responsible filter's value for "nobody is responsible". */
export const UNASSIGNED_RESPONSIBLE_FILTER = 'none';
/** The release filter's value for "no target release" (F-0334). */
export const NO_RELEASE_FILTER = 'none';
/** The priority filter's value for "no priority set". */
export const NO_PRIORITY_FILTER = 'none';

/** A narrowing the list applies itself, because `WorkItemFilterInput` has no field for it. */
export type ClientRefinementType = 'updated' | 'no-priority' | 'responsible';

export interface IListNarrowingInput {
  activeTab: WorkItemTabType;
  typeKey: string;
  responsibleFilter: string;
  priorityFilter: string;
  updatedFilter: WorkItemUpdatedFilterType;
  stateGroups: readonly WorkItemStateGroupType[];
}

export interface IServerListNarrowing {
  /** What goes to `workItemsPage(filter:)`, or `null` when nothing the list narrows by can. */
  filter: IWorkItemFilter | null;
  /** What the list still narrows on the client, over whatever population it draws. */
  clientRefinements: ClientRefinementType[];
}

/**
 * Splits the list's narrowing into what the server can say and what only the client can
 * (F-0231, AC-6.1 and AC-6.2).
 *
 * ⛔ **Every part the server can say goes to the server.** A client-only part next to it does not
 * pull the rest back to the client: the server narrows, and the client refines those pages. Both
 * are exact, because a refinement over a superset the server already narrowed removes only what
 * the refinement itself excludes.
 *
 * The server's filter is read by `workItemListFilterFrom` (T-0316). Its vocabulary sets the limits
 * here: a person or an agent by id, nobody, the four priorities, the five state groups, the type.
 * An agent named by key, an external identity, "no priority" and a last-updated window have no
 * field, so they stay client-side.
 *
 * Next up is its own queue with its own endpoint, so it is never narrowed here.
 */
export function serverListNarrowing(input: IListNarrowingInput): IServerListNarrowing {
  if (input.activeTab === 'next-up') return { filter: null, clientRefinements: [] };

  const filter: IWorkItemFilter = {};
  const clientRefinements: ClientRefinementType[] = [];

  if (input.activeTab === 'type' && input.typeKey) filter.itemTypeKey = input.typeKey;

  const stateGroups = STATE_GROUP_ORDER.filter((group) => input.stateGroups.includes(group));
  if (stateGroups.length > 0) filter.stateGroups = stateGroups;

  const responsible = input.responsibleFilter;
  if (responsible === UNASSIGNED_RESPONSIBLE_FILTER) filter.unassigned = true;
  else if (responsible.startsWith('user:')) filter.responsibleUserId = responsible.slice(5);
  else if (responsible.startsWith('agent:')) filter.responsibleAgentId = responsible.slice(6);
  else if (responsible) clientRefinements.push('responsible');

  if (input.priorityFilter === NO_PRIORITY_FILTER) clientRefinements.push('no-priority');
  else if (input.priorityFilter) filter.priorities = [input.priorityFilter];

  if (input.updatedFilter !== 'all') clientRefinements.push('updated');

  return {
    filter: Object.keys(filter).length > 0 ? filter : null,
    clientRefinements,
  };
}

/**
 * The release filter's ids: one target release, or several comma-separated (F-0546: the Parts tree's
 * *Next release* links the roadmap's Next, which a phased version line makes several releases).
 */
export function releaseFilterIds(release: string): string[] {
  return release.split(',').filter(Boolean);
}

/**
 * One status by key and a target release (F-0334), as the view engine filters them — the words the
 * Parts tree asks its counts in (`partItemViewFilters`), so a heading counts what the tree counted.
 */
export function statusAndReleaseViewFilters(
  statusKey: string,
  release: string,
  roadmapRelease = '',
): IViewFilterSpec[] {
  const filters: IViewFilterSpec[] = [];
  if (statusKey) filters.push({ fieldKey: 'workflowStateKey', operator: 'in', value: [statusKey] });
  if (release === NO_RELEASE_FILTER) {
    filters.push({ fieldKey: 'targetReleaseId', operator: 'is-null' });
  } else if (release) {
    filters.push({ fieldKey: 'targetReleaseId', operator: 'in', value: releaseFilterIds(release) });
  }
  // F-0546: where the roadmap draws the item — the engine's own derived field, never re-derived.
  if (roadmapRelease) {
    filters.push({
      fieldKey: 'roadmapReleaseId',
      operator: 'in',
      value: releaseFilterIds(roadmapRelease),
    });
  }
  return filters;
}

/**
 * Whether an item is in one status by key and a target release; `''` passes anything. A waiting
 * item's status is Waiting, as the server reads it (D-0159 a), so it answers to `waiting` and not to
 * the status it will return to.
 */
export function matchesStatusAndRelease(
  item: {
    state?: { key?: string | null } | null;
    targetReleaseId?: string | null;
    fixedInReleaseId?: string | null;
  },
  statusKey: string,
  release: string,
  waiting = false,
  roadmap: { release: string; statusOf: (releaseId: string) => string | null } = {
    release: '',
    statusOf: () => null,
  },
): boolean {
  const key = waiting ? WAITING_STATUS.key : item.state?.key;
  if (statusKey && key !== statusKey) return false;
  if (roadmap.release) {
    // F-0546: the release the roadmap draws the item under, by its rule (`roadmapReleaseIdFor`).
    const placed = roadmapReleaseIdFor({
      fixedInReleaseId: item.fixedInReleaseId,
      targetReleaseId: item.targetReleaseId,
      targetReleaseStatus: item.targetReleaseId ? roadmap.statusOf(item.targetReleaseId) : null,
    });
    if (!placed || !releaseFilterIds(roadmap.release).includes(placed)) return false;
  }
  if (release === NO_RELEASE_FILTER) return !item.targetReleaseId;
  if (release) return releaseFilterIds(release).includes(item.targetReleaseId ?? '');
  return true;
}
